import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Bell, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { alert as hapticAlert, critical as hapticCritical } from "@/lib/haptics";

/**
 * In-app toast for push notifications.
 * Opens its own WebSocket to /api/notifications/ws and shows a sliding
 * toast at the top of the viewport (below safe-area) when a 'notification'
 * message arrives — so users who have the app in foreground don't miss
 * the event silently.
 *
 * Auto-dismisses after 5s. Stacks up to 3 toasts; older ones fade out.
 * No-op for super_admin (uses different alert channel).
 */
export default function NotificationToaster() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  useEffect(() => {
    if (!user || user.role === "super_admin") return;
    const token = localStorage.getItem("altiv_token");
    if (!token) return;
    const apiUrl = import.meta.env.VITE_BACKEND_URL || "";
    const wsBase = apiUrl.replace(/^http/, "ws");
    if (!wsBase) return;

    let ws;
    let retryTimer;
    let closed = false;

    const connect = () => {
      try {
        ws = new WebSocket(`${wsBase}/api/notifications/ws?token=${encodeURIComponent(token)}`);
      } catch {
        return;
      }
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg?.kind === "notification") {
            const id = ++idRef.current;
            const payload = msg.notification || msg.data || msg;
            const title = payload.title || payload.subject || t("notifications.defaultTitle");
            const body = payload.body || payload.message || payload.preview || "";
            const severity = (payload.severity || payload.priority || "").toLowerCase();
            setToasts((prev) => [...prev.slice(-2), { id, title, body }]);
            // Haptic feedback en Android — pattern más fuerte para
            // severidades críticas (AOG, BLOCKED, safety_impact).
            if (severity === "critical" || severity === "high") hapticCritical();
            else hapticAlert();
            setTimeout(() => {
              setToasts((prev) => prev.filter((t) => t.id !== id));
            }, 5000);
          }
        } catch {}
      };
      ws.onclose = () => {
        if (closed) return;
        retryTimer = setTimeout(connect, 5000);
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch {}
      };
    };
    connect();
    return () => {
      closed = true;
      clearTimeout(retryTimer);
      try {
        ws && ws.close();
      } catch {}
    };
  }, [user]);

  if (!toasts.length) return null;

  const dismiss = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-[60] flex flex-col gap-2 w-full max-w-md px-3 pointer-events-none"
      style={{ top: "calc(env(safe-area-inset-top, 0px) + 8px)" }}
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          className="pointer-events-auto bg-[#0f172a] border border-[#0052FF]/40 rounded-xl shadow-2xl px-4 py-3 flex items-start gap-3 animate-slide-down"
        >
          <div className="w-8 h-8 rounded-lg bg-[#0052FF]/20 flex items-center justify-center flex-shrink-0">
            <Bell className="w-4 h-4 text-[#4d8cff]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">{t.title}</p>
            {t.body && <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{t.body}</p>}
          </div>
          <button
            onClick={() => dismiss(t.id)}
            className="w-11 h-11 sm:w-7 sm:h-7 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 flex items-center justify-center flex-shrink-0"
            aria-label="dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
